// directive
//   <stream-diagnostics broadcast="broadcast" />
//
import {
  ComputePastBroadcastStarRating,
  ComputePastBroadcastRecommendations,
  GetBitrateLimitsForBroadcast,
  V3Timestamps,
  V3Values,
  V3PlaceholderTimestamps,
  HasIntegratedScoreboard,
  V3HasAnyScoreboardData,
  ParseScoreboardEvents,
  FilterRSSIForLinkStatus,
  BaselineIngestLatency,
} from '../utils/stream-diagnostics';
import { SanitizeRTMPURL } from '../utils/rtmp';
import { getAuthToken } from '../auth/utils';

const RELOAD_INTERVAL_MS = 30000;

// The current implementation limits read sizes to 1 byte and completely ignores the message. This
// is just some data to keep the socket alive.
const WEBSOCKET_PING_MESSAGE = 'x';

const WEBSOCKET_PING_INTERVAL_MS = 10000;
const WEBSOCKET_ERROR_DELAY_MS = 10000;

const SCOREBOARD_ASSIST_FIRMWARE_IMAGE_NAME = 'wsa2_firmware.bin';

const xAxisTimeCfg = {
  type: 'time',
  gridLines: { display: false, drawBorder: false },
  position: 'bottom',
  distribution: 'series',
  source: 'auto',
  time: {
    unit: 'minute',
    displayFormats: {
      minute: 'h:mm a',
    },
  },
  ticks: {
    fontSize: 11,
    fontFamily: 'Arial, sans-serif',
    autoSkip: true,
  },
};

angular.module('app.directives').component('streamDiagnostics', {
  restrict: 'E',
  bindings: {
    broadcast: '=',
    onReload: '=',
  },
  template: require('./stream-diagnostics.html'),
  controllerAs: 'vm',
  controller: function StreamDiagnosticsController(
    $http,
    $interval,
    $q,
    $timeout,
    $scope,
    $rootScope,
    ScoreboardAssistFirmware
  ) {
    'ngInject';
    this.broadcast; //<-- injected via binding

    this.graphType = this.broadcast.timeframe == 'past' ? 'coarse' : 'fine';
    this.timeoutHandle = null;
    this.activeTab = 'stream health';

    // This may be overridden later if there's scoreboard data for this broadcast, even if it's currently disabled.
    this.showScoreboard = false;

    // Will be set later if/when we receive valid scoreboard data
    this.scoreboardMode = null;

    $scope.$on('$destroy', () => $timeout.cancel(this.timeoutHandle));

    this.latestScoreboardAssistFirmwareVersion = null;
    if (
      this.broadcast.timeframe != 'past' &&
      (this.broadcast.scoreboard_configuration || {}).scoreboard_make == 'BoxCast'
    ) {
      // If we're using the scoreboard assist, go fetch the latest firmware version
      ScoreboardAssistFirmware.version(
        { id: SCOREBOARD_ASSIST_FIRMWARE_IMAGE_NAME },
        (resp) => {
          this.latestScoreboardAssistFirmwareVersion = resp.version;
        },
        (e) => {
          console.warn('Error getting latest Scoreboard Assist firmware version', e);
        }
      );
    }

    this.pastSummary = () => {
      switch ((this.streamDiagnostics || {}).summary_status) {
        case 'OK':
          return { icon: '', text: '' };
        case 'Warning':
          return { icon: '', text: '' };
        case 'Critical':
          return { icon: '', text: '' };
        default:
          return { icon: '', text: '' };
      }
    };

    this.alertsFilter = (value) => value.is_active;

    this.updateGraph = () => {
      if (this.streamDiagnostics) {
        processGraphData();
      }
    };

    this.handleNewData = (data) => {
      this.streamDiagnostics = data;
      if (this.streamDiagnostics) {
        BaselineIngestLatency(this.streamDiagnostics, this.broadcast);

        this.scoreboardMode = (
          (this.streamDiagnostics.scoreboard || {}).scoreboard_assist || {}
        ).operation_mode;

        // If this.showScoreboard is false, make it true if there's data here
        this.showScoreboard =
          (this.streamDiagnostics.data_version >= 3 &&
            HasIntegratedScoreboard(this.broadcast.scoreboard_configuration)) ||
          V3HasAnyScoreboardData(this.streamDiagnostics);

        processGraphData();

        if (this.broadcast.timeframe == 'past') {
          this.pastBroadcastStarCount = ComputePastBroadcastStarRating(
            this.streamDiagnostics || {}
          );
          this.pastBroadcastRecommendations = ComputePastBroadcastRecommendations(
            this.broadcast,
            this.streamDiagnostics || []
          );
        }

        $timeout(() => this.onReload(this.streamDiagnostics));
      }
    };

    this.loadViaGet = () => {
      return $http
        .get(`${API_ROOT}/account/broadcasts/${this.broadcast.id}/stream_diagnostics`)
        .then((r) => {
          this.handleNewData(r.data);

          // Decide on whether or not to auto-reload
          if (this.broadcast.timeframe != 'past') {
            $timeout.cancel(this.timeoutHandle);
            this.timeoutHandle = $timeout(() => this.loadViaGet(), RELOAD_INTERVAL_MS);
          }
        })
        .catch((err) => {
          console.error('Error getting stream diagnostics', err);

          // Try again after the reload interval
          $timeout.cancel(this.timeoutHandle);
          this.timeoutHandle = $timeout(() => this.loadViaGet(), RELOAD_INTERVAL_MS);
        });
    };

    // Returns a promise indicating whether or not we successfully got the WebSocket set up. If
    // not, load falls back to using HTTP.
    this.loadViaWebSocket = () => {
      const d = $q.defer();

      // First, get the websocket address from the API
      $http
        .get(`${API_ROOT}/account/broadcasts/${this.broadcast.id}/stream_diagnostics/wss_endpoint`)
        .then((r) => {
          // Second, open the websocket to the address
          const wssAddress = r.data;
          const makeWebSocket = () => {
            const ws = new WebSocket(
              wssAddress + '?access_token=' + encodeURIComponent(getAuthToken())
            );
            let pingInterval = null;
            let scopeDestroying = false;
            let opened = false;
            ws.addEventListener('open', (e) => {
              opened = true;
              pingInterval = $interval(
                () => ws.send(WEBSOCKET_PING_MESSAGE),
                WEBSOCKET_PING_INTERVAL_MS,
                0,
                false
              );
            });
            ws.addEventListener('close', (e) => {
              if (!scopeDestroying) {
                console.warn('Web socket closed unexpectedly', e);
                $interval.cancel(pingInterval);
                if (opened) {
                  // If we DID successfully open the web socket, then just try again pretty much
                  // right away, as we likely just got disconnected
                  $timeout(() => {
                    try {
                      makeWebSocket();
                    } catch (err) {
                      console.error('Error creating the web socket', err);
                      remoteLogger.error('Error creating the web socket', { error: err });
                      this.loadViaGet();
                    }
                  });
                } else {
                  // If we didn't actually open the web socket, the broadcast might have transitioned
                  // to past/VOD. Retry from a higher level after a timeout.
                  $timeout(this.load, WEBSOCKET_ERROR_DELAY_MS);
                }
              }
            });
            let lastRawStatsCoarse = null;
            let lastRawScoreboardStatsCoarse = null;
            ws.addEventListener('message', (e) => {
              let data = null;
              try {
                data = JSON.parse(e.data);
              } catch (err) {
                console.error('Error parsing stream diagnostics data', err);
              }
              if (data) {
                // Not every update will contain coarse data, as the stream diagnostics service only
                // refreshes this every ~minute. So hang onto the latest one and glue it on when it's
                // missing.
                if (data.raw_stats_coarse) {
                  lastRawStatsCoarse = data.raw_stats_coarse;
                } else {
                  data.raw_stats_coarse = lastRawStatsCoarse;
                }
                if (data.raw_scoreboard_stats_coarse) {
                  lastRawScoreboardStatsCoarse = data.raw_scoreboard_stats_coarse;
                } else {
                  data.raw_scoreboard_stats_coarse = lastRawScoreboardStatsCoarse;
                }
                this.handleNewData(data);
              }
            });
            $scope.$on('$destroy', () => {
              scopeDestroying = true;
              $interval.cancel(pingInterval);
              ws.close(1000);
            });
          };
          try {
            makeWebSocket();
            d.resolve(true);
          } catch (err) {
            console.error('Error creating the web socket', err);
            remoteLogger.error('Error creating the web socket', { error: err });
            d.reject(err);
          }
        })
        .catch((err) => {
          console.error('Error getting stream diagnostics web socket address', err);
          d.reject(err);
        });

      return d.promise;
    };

    this.load = () => {
      if (!this.broadcast.id) {
        console.warn(
          'Broadcast ID not available. Did you pass in an unresolved promise on accident?'
        );
        return;
      }
      if (this.broadcast.timeframe == 'future') {
        console.warn('Diagnostics not applicable for future broadcast');
        return;
      }

      // Figure out whether or not to open the websocket connection. Only do that if the broadcast
      // is current or preroll and web sockets appear to be supported.
      const supportsWebSockets = 'WebSocket' in window;
      if (
        (this.broadcast.timeframe === 'preroll' || this.broadcast.timeframe === 'current') &&
        supportsWebSockets
      ) {
        this.loadViaWebSocket().catch(this.loadViaGet);
        return;
      }

      this.loadViaGet();
    };

    const statValuesForIndex = (stats, i, j, series = 0) => {
      var r = ((stats || {}).results || [])[i];
      if (!r || !r.series || !r.series.length) return [];
      return _.map(r.series[series].values, (t) => t[j]);
    };

    const processGraphData = () => {
      var stats = this.streamDiagnostics.raw_stats;
      if (this.graphType == 'coarse') {
        stats = this.streamDiagnostics.raw_stats_coarse;
      }
      const dataVersion = this.streamDiagnostics.data_version;
      const bitrateLimits = GetBitrateLimitsForBroadcast($rootScope.ApiConfig, this.broadcast);
      const hasAnyData = (data) => !!data && data.length;
      const hasAnyDataOrNull = (data) => (hasAnyData(data) ? data : null);
      let bitrateSuggestedMax = 0;
      if (dataVersion >= 3) {
        this.bitrateData = [];
        const videoBitrateData = V3Values(stats, 'bitrate', 'video');
        if (hasAnyData(videoBitrateData)) {
          this.bitrateData.push(videoBitrateData);
          bitrateSuggestedMax += (bitrateLimits.video || {}).nominal || 6000000;
        }
        const audioBitrateData = V3Values(stats, 'bitrate', 'audio');
        if (hasAnyData(audioBitrateData)) {
          this.bitrateData.push(audioBitrateData);
          bitrateSuggestedMax += (bitrateLimits.audio || {}).nominal || 128000;
        }
        this.bitrateLabels =
          hasAnyDataOrNull(V3Timestamps(stats, 'bitrate', 'video')) ||
          hasAnyDataOrNull(V3Timestamps(stats, 'bitrate', 'audio'));

        this.framerateData = [];
        const videoFrameRateData = V3Values(stats, 'frame_rate', 'video');
        if (videoFrameRateData) this.framerateData.push(videoFrameRateData);
        // XXX: Although we likely have audio and score "frame" rate data, we pursposefully don't show it, as it's
        // potentially confusing to users.
        this.framerateLabels = V3Timestamps(stats, 'frame_rate', 'video');

        this.ingestLatencyData = [];
        this.ingestLatencyLabels = [];
        if (stats.ingest_latency) {
          this.ingestLatencyData.push(V3Values(stats, 'ingest_latency'));
          this.ingestLatencyLabels = V3Timestamps(stats, 'ingest_latency');
        }

        this.plData = [];
        this.totalPacketsLost = 0;
        const videoPacketLossData = V3Values(stats, 'packet_loss', 'video');
        if (hasAnyData(videoPacketLossData)) {
          this.plData.push(videoPacketLossData);
          this.totalPacketsLost += _.sum(videoPacketLossData);
        }
        const audioPacketLossData = V3Values(stats, 'packet_loss', 'audio');
        if (hasAnyData(audioPacketLossData)) {
          this.plData.push(audioPacketLossData);
          this.totalPacketsLost += _.sum(audioPacketLossData);
        }
        const scorePacketLossData = V3Values(stats, 'packet_loss', 'score');
        if (hasAnyData(scorePacketLossData) && this.showScoreboard) {
          this.plData.push(scorePacketLossData);
          this.totalPacketsLost += _.sum(scorePacketLossData);
        }
        this.plLabels =
          hasAnyDataOrNull(V3Timestamps(stats, 'packet_loss', 'video')) ||
          hasAnyDataOrNull(V3Timestamps(stats, 'packet_loss', 'audio'));

        this.simulcastData = [];
        this.simulcastLabels = [];
        const rtmpURLs = _.keys(stats.simulcast_frame_rate || {}).sort();
        if (rtmpURLs.length > 0) {
          // XXX: Since simulcasts can be added and removed, and they're collected with fill(none), each RTMP URL
          // can potentially have a different set of timestamps than the others. Here we collect all the timestamps
          // and make sure that each series gets a placeholder for each timestamp.
          this.simulcastLabels = _.uniq(
            _.flatten(
              _.map(_.values(stats.simulcast_frame_rate || {}), (v) =>
                _.flatten([V3Timestamps(v, 'video'), V3Timestamps(v, 'audio')])
              )
            )
          ).sort();
          _.each(rtmpURLs, (u) => {
            var s = stats.simulcast_frame_rate[u];
            const videoSimulcastData = V3Values(s, 'video');
            if (hasAnyData(videoSimulcastData)) {
              const videoSimulcastTimestamps = V3Timestamps(s, 'video');
              this.simulcastData.push(
                V3PlaceholderTimestamps(
                  videoSimulcastData,
                  videoSimulcastTimestamps,
                  this.simulcastLabels
                )
              );
            }
          });
        }
      } else {
        const valuesForIndex = (i, j, series = 0) => statValuesForIndex(stats || {}, i, j, series);

        this.bitrateData = [
          _([]).concat(valuesForIndex(0, 1), valuesForIndex(1, 1), valuesForIndex(2, 1)).value(),
        ];
        this.bitrateLabels = _([])
          .concat(valuesForIndex(0, 0), valuesForIndex(1, 0), valuesForIndex(2, 0))
          .value();

        this.framerateData = [valuesForIndex(3, 1)];
        this.framerateLabels = valuesForIndex(3, 0);

        this.ingestLatencyData = [];
        this.ingestLatencyLabels = [];

        this.plData = [valuesForIndex(7, 1), valuesForIndex(8, 1)];
        this.plLabels = valuesForIndex(7, 0);
        this.totalPacketsLost = _.sum(this.plData[0]) + _.sum(this.plData[1]);

        // Multiple series support for simulcast by destination
        if (
          stats.results &&
          stats.results[4] &&
          stats.results[4].series &&
          stats.results[4].series.length
        ) {
          this.simulcastData = _.map(stats.results[4].series, (s, i) => {
            return valuesForIndex(4, 1, i);
          });
          this.simulcastLabels = valuesForIndex(4, 0);
        }

        bitrateSuggestedMax =
          ((bitrateLimits.video || {}).nominal || 6000000) +
          ((bitrateLimits.audio || {}).nominal || 128000);
      }

      const expectedVideoFrameRate = (this.broadcast.transcoder_profile || '').match(/p60/)
        ? 60
        : 30;

      this.plOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                suggestedMax: 100,
                beginAtZero: true,
                // use monospace font and 4 characters to align the graphs
                callback: function (value, index, values) {
                  return padOut(Math.round(value), 4);
                },
                fontFamily: 'monospace',
                fontSize: 8,
              },
              stacked: true,
            },
          ],
          xAxes: [_.extend({}, xAxisTimeCfg, { stacked: true })],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.plLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              let kind = ['video ', 'audio ', 'serial '][item.datasetIndex] || '';
              return `${item.yLabel} ${kind}packets lost`;
            },
          },
        },
      };

      this.bitrateOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                suggestedMax: bitrateSuggestedMax,
                beginAtZero: true,
                // use monospace font and 4 characters to align the graphs
                callback: function (value, index, values) {
                  return padOut(Math.round(value / 1000), 4);
                },
                fontFamily: 'monospace',
                fontSize: 8,
              },
              stacked: true,
            },
          ],
          xAxes: [_.extend({}, xAxisTimeCfg, { stacked: true })],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.bitrateLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              let kind = '';
              if (this.bitrateData && this.bitrateData.length > 1) {
                kind = ['Video: ', 'Audio: '][item.datasetIndex] || '';
              }
              return `${kind}${Math.round(item.yLabel / 1000)} kbps`;
            },
          },
        },
      };
      this.bitrateDatasetOverride = [
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',
        },
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',
        },
      ];

      this.framerateOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                suggestedMax: expectedVideoFrameRate,
                beginAtZero: true,
                // use monospace font and 4 characters to align the graphs
                callback: function (value, index, values) {
                  return padOut(Math.round(value), 4);
                },
                fontFamily: 'monospace',
                fontSize: 8,
              },
            },
          ],
          xAxes: [xAxisTimeCfg],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.framerateLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              return `${Math.round(100 * item.yLabel) / 100.0} FPS`;
            },
          },
        },
      };
      this.framerateDatasetOverride = [
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',
        },
      ];

      this.ingestLatencyOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                suggestedMin: 0,
                suggestedMax: 10,
                fontFamily: 'monospace',
                fontSize: 8,
              },
            },
          ],
          xAxes: [xAxisTimeCfg],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.framerateLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              if (Math.abs(item.yLabel) < 1.0) {
                return `${Math.round(1000 * item.yLabel)} ms`;
              }
              return `${Math.round(1000 * item.yLabel) / 1000.0} s`;
            },
          },
        },
      };
      this.ingestLatencyDatasetOverride = [
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',
        },
      ];

      this.simulcastOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                suggestedMax: expectedVideoFrameRate,
                beginAtZero: true,
                // use monospace font and 4 characters to align the graphs
                callback: function (value, index, values) {
                  return padOut(Math.round(value), 4);
                },
                fontFamily: 'monospace',
                fontSize: 8,
              },
            },
          ],
          xAxes: [xAxisTimeCfg],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.simulcastLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              var i = item.datasetIndex;
              var rtmpUrl;
              if (dataVersion >= 3) {
                rtmpUrl = _.keys(stats.simulcast_frame_rate || {}).sort()[i];
              } else {
                rtmpUrl = stats.results[4].series[i].tags.rtmp_url;
              }
              var rtmpLabel = SanitizeRTMPURL(rtmpUrl);
              if (rtmpUrl.indexOf('facebook.com') >= 0) {
                rtmpLabel = 'Facebook';
              } else if (rtmpUrl.indexOf('youtube.com') >= 0) {
                rtmpLabel = 'YouTube';
              } else if (rtmpUrl.indexOf('pscp.tv') >= 0 || rtmpUrl.indexOf('twitter') >= 0) {
                rtmpLabel = 'Twitter';
              }
              return `${Math.round(100 * item.yLabel) / 100.0} FPS to ${rtmpLabel}`;
            },
          },
        },
      };
      this.simulcastDatasetOverride = [];
      for (var i = 0; i < (this.simulcastData || []).length; i++) {
        this.simulcastDatasetOverride.push({
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',
        });
      }

      let scoreboardStats = this.streamDiagnostics.raw_scoreboard_stats;
      if (this.graphType == 'coarse') {
        scoreboardStats = this.streamDiagnostics.raw_scoreboard_stats_coarse;
      }

      this.scoreDataRateData = [];
      this.scoreDataRateLabels = [];
      if (dataVersion >= 3) {
        const scoreBitrateData = V3Values(stats, 'bitrate', 'score');
        if (hasAnyData(scoreBitrateData) && this.showScoreboard)
          this.scoreDataRateData.push(scoreBitrateData);
        this.scoreDataRateLabels = hasAnyDataOrNull(V3Timestamps(stats, 'bitrate', 'score'));
      }

      const scoreboardAssistStats = (scoreboardStats || {}).scoreboard_assist;
      FilterRSSIForLinkStatus(scoreboardAssistStats);
      const rssi = (scoreboardAssistStats || {}).rssi || {};
      const boxRSSITimestamps = V3Timestamps(rssi, 'box');
      const scoreboardRSSITimestamps = V3Timestamps(rssi, 'scoreboard');
      this.rssiLabels = _.uniq(_.flatten([boxRSSITimestamps, scoreboardRSSITimestamps])).sort();
      this.rssiData = [];
      const boxRSSIData = V3Values(rssi, 'box');
      if (hasAnyData(boxRSSIData))
        this.rssiData.push(
          V3PlaceholderTimestamps(boxRSSIData, boxRSSITimestamps, this.rssiLabels)
        );
      const scoreboardRSSIData = V3Values(rssi, 'scoreboard');
      if (hasAnyData(scoreboardRSSIData))
        this.rssiData.push(
          V3PlaceholderTimestamps(scoreboardRSSIData, scoreboardRSSITimestamps, this.rssiLabels)
        );

      this.scoreboardEvents = ParseScoreboardEvents(
        this.streamDiagnostics,
        this.broadcast.scoreboard_configuration
      );

      this.scoreDataRateOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                beginAtZero: true,
                // use monospace font and 4 characters to align the graphs
                callback: function (value, index, values) {
                  return padOut(value, 5);
                },
                fontFamily: 'monospace',
                fontSize: 8,
              },
            },
          ],
          xAxes: [xAxisTimeCfg],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.bitrateLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              return `${Math.round(item.yLabel)} bps`;
            },
          },
        },
      };
      this.scoreDataRateDatasetOverride = [
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',
        },
      ];

      this.rssiOptions = {
        legend: { display: false },
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: '#EEEEEE',
                zeroLineColor: '#EEEEEE',
                drawTicks: false,
                drawBorder: false,
                tickMarkLength: 0,
              },
              ticks: {
                // use monospace font and 4 characters to align the graphs
                callback: function (value, index, values) {
                  return padOut(Math.round(value), 4);
                },
                fontFamily: 'monospace',
                fontSize: 8,
              },
            },
          ],
          xAxes: [xAxisTimeCfg],
        },
        tooltips: {
          callbacks: {
            title: (items, data) => {
              return moment(this.framerateLabels[items[0].index]).format('ddd MMM Do, h:mm:ss a');
            },
            label: (item, data) => {
              let side = '';
              if (this.bitrateData && this.bitrateData.length > 1) {
                side = ['Box Side: ', 'Scoreboard Side: '][item.datasetIndex] || '';
              }
              return `${side}${Math.round(10 * item.yLabel) / 10.0} dBm`;
            },
          },
        },
      };
      this.rssiDatasetOverride = [
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          fill: false,
          cubicInterpolationMode: 'monotone',
        },
        {
          pointRadius: 0,
          pointHitRadius: 5,
          pointHoverRadius: 4,
          fill: false,
          cubicInterpolationMode: 'monotone',
        },
      ];
    };

    this.load();
  },
});

function padOut(str, length) {
  str = '' + str; //cast int to str
  while (str.length < length) {
    str = ' ' + str;
  }
  return str;
}



// WEBPACK FOOTER //
// ./app/directives/stream-diagnostics.js